import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apolloClient from "../graphql/apolloClient";
import { UserDetails, AuthContextType } from "../types/auth.types";
import { createRequestId, recordAuthPageAction, startTimer } from "../utils/perfTelemetry";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserDetails | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const elapsed = startTimer();
    const requestId = createRequestId();
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        recordAuthPageAction({
          action: "session_restore",
          outcome: "success",
          duration_ms: elapsed(),
          requestId,
          status: 200,
        });
      } catch {
        recordAuthPageAction({
          action: "session_restore",
          outcome: "failure",
          duration_ms: elapsed(),
          requestId,
          failure_stage: "parse_user_data",
          status: 0,
        });
      }
    } else {
      recordAuthPageAction({
        action: "session_restore",
        outcome: "not_found",
        duration_ms: elapsed(),
        requestId,
        status: 200,
      });
    }
  }, []);

  const login = (userDetails: UserDetails) => {
    setUser(userDetails);
    localStorage.setItem("userData", JSON.stringify(userDetails));
  };

  const logout = () => {
    const elapsed = startTimer();
    const requestId = createRequestId();
    setUser(null);
    localStorage.removeItem("userData");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.clear();
    sessionStorage.clear();
    // Clear Apollo Client cache
    apolloClient.cache.reset();
    recordAuthPageAction({
      action: "logout",
      outcome: "success",
      duration_ms: elapsed(),
      requestId,
      status: 200,
    });
    navigate("/login");
  };

  const updateUser = (userDetails: UserDetails) => {
    setUser(userDetails);
    localStorage.setItem("userData", JSON.stringify(userDetails));
  };

  return (
    <AuthContext.Provider value={{ user, setUser: updateUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
